import { StaticImage } from 'gatsby-plugin-image'
import React, { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css, useTheme } from 'styled-components'
import { hexToRGBA } from './sc-theme'
import { Container } from '../components/container'

export const Footer: FC = () => {
  const { t } = useTranslation('footer')
  const {
    colors: { light },
  } = useTheme()

  return (
    <StyledFooter>
      <FooterContainer>
        <FooterTitle>Exclusive Maintenance</FooterTitle>
        <FooterContact>
          <div className="social">
            <a
              href="https://www.facebook.com/Exclusive-Maintenance-102445998837046/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                data-name="Ebene 1"
                viewBox="0 0 1024 1024"
              >
                <path
                  className="facebook-circle"
                  fill={light}
                  d="M1024,512C1024,229.23016,794.76978,0,512,0S0,229.23016,0,512c0,255.554,187.231,467.37012,432,505.77777V660H302V512H432V399.2C432,270.87982,508.43854,200,625.38922,200,681.40765,200,740,210,740,210V336H675.43713C611.83508,336,592,375.46667,592,415.95728V512H734L711.3,660H592v357.77777C836.769,979.37012,1024,767.554,1024,512Z"
                />
                <path
                  className="facebook-f"
                  fill="none"
                  d="M711.3,660,734,512H592V415.95728C592,375.46667,611.83508,336,675.43713,336H740V210s-58.59235-10-114.61078-10C508.43854,200,432,270.87982,432,399.2V512H302V660H432v357.77777a517.39619,517.39619,0,0,0,160,0V660Z"
                />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/exclusivemaintenance/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                <path
                  fill={light}
                  d="M32 31.292V19.46c0-6.34-3.384-9.29-7.896-9.29-3.641 0-5.273 2.003-6.182 3.409v-2.924h-6.86c.091 1.937 0 20.637 0 20.637h6.86V19.767c0-.615.044-1.232.226-1.672.495-1.233 1.624-2.509 3.518-2.509 2.483 0 3.475 1.892 3.475 4.666v11.041H32v-.001zM3.835 7.838c2.391 0 3.882-1.586 3.882-3.567C7.673 2.247 6.227.707 3.881.707S0 2.246 0 4.271c0 1.981 1.489 3.567 3.792 3.567h.043zm3.43 23.454V10.655H.406v20.637h6.859z"
                />
              </svg>
            </a>
          </div>
          <div className="methods">
            <a href="mailto:info@exclusivemaintenance.com">
              info@exclusivemaintenance.com
            </a>
            <a href="tel:1-514-744-6991">514-744-6991</a>
          </div>
        </FooterContact>
        <FooterWebsite>
          &copy; 2021 Exclusive Maintenance.
          <br />
          {t('rights')}
          <br />
          <Trans i18nKey="footer:designedBy">
            <a
              href="https://sajj.studio/"
              target="_blank"
              rel="noopener noreferrer"
            >
              SAJJ.studio
            </a>
          </Trans>
        </FooterWebsite>
        <FooterPartners>
          <StaticImage
            src="../assets/images/issa.png"
            alt="ISSA CIMS"
            className="issa"
            layout="constrained"
            objectFit="contain"
            width={184}
          />
          <StaticImage
            src="../assets/images/cogni.png"
            alt="Cognibox"
            className="cogni"
            layout="constrained"
            objectFit="contain"
            width={184}
          />
          <StaticImage
            src="../assets/images/check.png"
            alt="Contractor Check"
            className="check"
            layout="constrained"
            objectFit="contain"
            width={184}
          />
        </FooterPartners>
      </FooterContainer>
    </StyledFooter>
  )
}

const StyledFooter = styled.footer`
  ${({ theme }) => css`
    background: ${hexToRGBA(theme.colors.light, 0.1)};
    color: ${theme.colors.light};
    line-height: 1.33;
  `}
`

const FooterContainer = styled(Container)`
  ${({ theme }) => css`
    ${theme.media.min('tablet')} {
      display: grid;
      grid-template-areas:
        'title title'
        'contact .'
        'website partners';

      a:hover {
        color: ${theme.colors.accent};

        svg path {
          fill: ${theme.colors.accent};
        }
      }
    }
  `}
`

const FooterTitle = styled.div`
  ${({ theme }) => css`
    font-size: 1.125rem;
    font-weight: 500;
    text-transform: uppercase;
    border-bottom: 2px solid ${theme.colors.accent};
    padding-bottom: 0.25rem;

    ${theme.media.max('tablet')} {
      width: fit-content;
    }

    ${theme.media.min('tablet')} {
      grid-area: title;
    }
  `}
`

const FooterContact = styled.div`
  ${({ theme }) => css`
    font-size: 1.125rem;
    padding-bottom: 0 0 2.25rem 0;

    ${theme.media.min('tablet')} {
      display: flex;
      align-items: center;
      padding: 1.5rem 0 1rem;
      grid-area: contact;
    }

    .social {
      ${theme.media.min('tablet')} {
        padding-right: 0.5rem;
      }

      svg {
        height: 2.75rem;
        width: 2.75rem;
        display: inline-block;
        margin: 0.875rem 1rem 0.875rem 0;
      }
    }
    .methods {
      a {
        display: block;
        text-decoration: none;
      }
    }
  `}
`

const FooterWebsite = styled.div`
  ${({ theme }) => css`
    font-size: 0.875rem;
    padding-bottom: 1.875rem;

    ${theme.media.min('tablet')} {
      grid-area: website;
    }
  `}
`

const FooterPartners = styled.div`
  ${({ theme }) => css`
    max-width: 25rem;
    display: grid;
    grid-column-gap: 2rem;
    grid-template-areas:
      'issa cogni'
      'issa check';

    .issa {
      grid-area: issa;
      width: 100%;
    }
    .cogni {
      grid-area: cogni;
      width: 100%;
    }
    .check {
      grid-area: check;
      width: 100%;
    }

    ${theme.media.min('tablet')} {
      grid-area: partners;
    }
  `}
`
